import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

const MAXIMUM_LENGTH = 100

const hiddenAddressField = (isRequired: boolean) =>
  isRequired ? requiredField() : z.string().trim().optional()

const requiredField = () =>
  z.string().trim().min(1, i18n.t('benefits.healthcare.plan_wizard.validation.common.required'))

const maxLengthField = () =>
  z
    .string()
    .trim()
    .max(
      MAXIMUM_LENGTH,
      i18n.t('benefits.healthcare.plan_wizard.validation.common.max_length', {
        max: MAXIMUM_LENGTH,
      })
    )

export const personalInformationFieldSchemas = (showEmployeeAddressFields = false) => ({
  firstName: requiredField().pipe(maxLengthField()),
  lastName: requiredField().pipe(maxLengthField()),
  sex: z.enum(['male', 'female'], {
    required_error: i18n.t('benefits.healthcare.plan_wizard.validation.common.required'),
  }),
  birthday: requiredField().refine((date) => {
    const birthDate = new Date(date)
    const now = new Date()
    return birthDate <= now
  }, i18n.t('benefits.healthcare.enrollment_wizard.validation.common.date_not_in_past')),
  addressLine1: requiredField().pipe(maxLengthField()),
  addressLine2: maxLengthField().optional(),
  postalCode: hiddenAddressField(showEmployeeAddressFields),
  city: hiddenAddressField(showEmployeeAddressFields),
  province: hiddenAddressField(showEmployeeAddressFields),
  taxIdNumber: requiredField(),
  taxIdType: requiredField(),
  email: requiredField()
    .pipe(
      z
        .string()
        .email(i18n.t('benefits.healthcare.enrollment_wizard.validation.common.invalid_email'))
    )
    .pipe(maxLengthField()),
  phoneNumber: requiredField().regex(
    /^\+[\d ()-]+$/,
    i18n.t('benefits.healthcare.enrollment_wizard.validation.phone_number.invalid_format')
  ),
  iban: z
    .string()
    .transform((value) => value.replace(/\s/g, ''))
    .pipe(
      z
        .string()
        .min(1, i18n.t('benefits.healthcare.plan_wizard.validation.common.required'))
        .min(6, i18n.t('benefits.healthcare.enrollment_wizard.validation.invalid_iban'))
        .max(34, i18n.t('benefits.healthcare.enrollment_wizard.validation.invalid_iban'))
        .regex(
          /^(?=.{15,34}$)[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/,
          i18n.t('benefits.healthcare.enrollment_wizard.validation.invalid_iban')
        )
    ),
  memberType: z.literal('employee'),
})

// Personal Information Step Schema
export const personalInformationStepSchema = (showEmployeeAddressFields = false) =>
  z.object(personalInformationFieldSchemas(showEmployeeAddressFields))

export const hasMissingEmployeeAddressData = (data: {
  postalCode?: string
  city?: string
  province?: string
}) => !data.postalCode || !data.city || !data.province

export const normalizeHiddenEmployeeAddressFields = <T extends Record<string, unknown>>(
  data: T & {
    postalCode?: string
    city?: string
    province?: string
  }
): Omit<T, 'postalCode' | 'city' | 'province'> & {
  postalCode: string
  city: string
  province: string
} => ({
  ...data,
  postalCode: data.postalCode ?? '',
  city: data.city ?? '',
  province: data.province ?? '',
})
